import React, { useEffect, useState, useMemo, useCallback } from "react";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import Pagination from "../../common/TablePagination";
import CreateNewButton from "../../common/CreateNewButton";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import {
  getFundraiserReferrals,
  getMerchantById,
} from "../../../services/fundraiserService";
import { matchesListTimePeriod } from "../../../utils/listTimePeriod";
import FilterPanel from "../../common/FilterPanel";
import { Dropdown } from "antd";
import SearchBar from "../../common/SearchBar";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
import { ReactComponent as PendingIcon } from "../../../Assets/Icons/pendingIcon.svg";
import { ReactComponent as AcceptedIcon } from "../../../Assets/Icons/acceptedIcon.svg";

import PopupContent from "./PopupContent";
import DetailsModal from "../../common/DetailsModal";
import { Drawer } from "antd";
import AddNewLocation from "../../common/OfferForm/Location/AddNewLocation";
import UploadCSVDrawer from "../../common/OfferForm/Location/UploadCSVDrawer";
import { addFranchise } from "../../../services/franchiseService";
import { ReactComponent as BackArrow } from "../../../Assets/Icons/backWhiteIcon.svg";
import toast from "../../../utils/toast";
import "../../common/OfferForm/offerform.scss";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import debounce from "lodash.debounce";
import TableEmptyState from "../../common/TableEmptyState";
import FilterButton from "../../common/FilterButton";
import ActiveFilterBar from "../../common/ActiveFilterBar";
import { Drawer as MuiDrawer } from "@mui/material";

const FundraiserReferralListTable = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const [allReferrals, setAllReferrals] = useState([]);
  const [fullRawData, setFullRawData] = useState([]);
  const [filteredDataList, setFilteredDataList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [merchantDetails, setMerchantDetails] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeMerchantId, setActiveMerchantId] = useState(null);
  const [csvLocations, setCsvLocations] = useState([]);
  const [selectedCsvLocationIds, setSelectedCsvLocationIds] = useState([]);
  const [locationToEdit, setLocationToEdit] = useState(null);
  const [isCsvSubmitting, setIsCsvSubmitting] = useState(false);
  const [addClicked, setAddClicked] = useState(false);
  const [csvDrawerOpen, setCsvDrawerOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const [filterParams, setFilterParams] = useState({
    status: null,
    duration: null,
  });

  const filterData = useMemo(
    () => [
      {
        id: "status",
        title: "Status",
        options: ["Accepted", "Pending"],
        defaultValue: null,
        multiSelect: false,
        valueMap: {
          "Accepted": "accepted",
          "Pending": "pending",
        },
      },
      {
        id: "duration",
        title: "Duration",
        options: ["Today", "This Week", "This Month", "This Year"],
        defaultValue: null,
        multiSelect: false,
        valueMap: {
          "Today": "DAY",
          "This Week": "WEEK",
          "This Month": "MONTH",
          "This Year": "YEAR",
        },
      },
    ],
    [],
  );

  // Create initial state from filter data
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        acc[filter.id] = filter.defaultValue ?? null;
      }
      return acc;
    }, {});
  };

  const [selections, setSelections] = useState(createInitialState(filterData));

  const activeFilterChips = useMemo(() => {
    const chips = [];
    Object.entries(filterParams).forEach(([key, value]) => {
      if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) return;
      
      const filter = filterData.find((f) => f.id === key);
      if (!filter) return;

      if (filter.multiSelect && Array.isArray(value)) {
        value.forEach((val) => {
          const displayLabel = Object.keys(filter.valueMap || {}).find(k => filter.valueMap[k] === val) || val;
          chips.push({ id: key, value: val, label: `${displayLabel}` });
        });
      } else {
        const displayLabel = Object.keys(filter.valueMap || {}).find(k => filter.valueMap[k] === value) || value;
        chips.push({ id: key, value: value, label: `${displayLabel}` });
      }
    });
    return chips;
  }, [filterParams, filterData]);

  const activeCount = activeFilterChips.length;

  const mapFilterValue = useCallback(
    (filterId, value) => {
      const filter = filterData.find((item) => item.id === filterId);
      if (!filter?.valueMap || value === null || value === undefined) {
        return value;
      }

      if (Array.isArray(value)) {
        return value.map((item) => filter.valueMap[item] ?? item);
      }

      return filter.valueMap[value] ?? value;
    },
    [filterData],
  );

  const applyFilters = (sels) => {
    setFilterParams({
      status: mapFilterValue("status", sels.status),
      duration: mapFilterValue("duration", sels.duration),
    });
    setCurrentPage(1);
  };

  const handleApply = () => {
    applyFilters(selections);
    setOpen(false);
  };

  const handleReset = () => {
    const initialState = createInitialState(filterData);
    setSelections(initialState);
    setFilterParams({ status: null, duration: null });
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleRemoveFilter = (targetChip) => {
    const filter = filterData.find((f) => f.id === targetChip.id);
    if (!filter) return;

    let newValue;
    if (filter.multiSelect) {
      newValue = selections[targetChip.id].filter((v) => v !== targetChip.value);
    } else {
      newValue = null;
    }

    const newSelections = { ...selections, [targetChip.id]: newValue };
    setSelections(newSelections);
    applyFilters(newSelections);
  };

  const items = [
    {
      title: "Refer a Merchant",
    },
    {
      title: "Referral List",
    },
  ];

  const columns = [
    { name: "No." },
    { name: "Name" },
    { name: "Date" },
    { name: "Email" },
    { name: "Contact" },
    { name: "Status" },
  ];

  const updatePageSlice = (data, page) => {
    if (!data) return;
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    setAllReferrals(data.slice(startIndex, endIndex));
  };

  const applyClientSideFilters = useCallback((data) => {
    let filtered = [...data];

    if (filterParams.status) {
      const sLower = filterParams.status.toLowerCase();
      filtered = filtered.filter((item) => {
        const itemStatus = (item.status || "").toLowerCase();
        if (sLower === "accepted") {
          return itemStatus === "accepted" || itemStatus === "approved" || itemStatus === "live";
        }
        return itemStatus === sLower;
      });
    }

    if (filterParams.duration) {
      filtered = filtered.filter((item) =>
        matchesListTimePeriod(item, filterParams.duration),
      );
    }

    setFilteredDataList(filtered);
    setCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / 10) || 1);
    setCurrentPage(1);
    setAllReferrals(filtered.slice(0, 10));
  }, [filterParams.duration, filterParams.status]);

  const getAllReferralsData = async (search = "") => {
    try {
      const params = {
        limit: 1000,
        search: search || null,
      };

      const response = await dispatch(getFundraiserReferrals(params)).unwrap();

      // EXHAUSTIVE DATA EXTRACTION
      let rawList = [];
      if (Array.isArray(response?.data)) {
        rawList = response.data;
      } else if (response?.data && Array.isArray(response.data.data)) {
        rawList = response.data.data;
      } else if (Array.isArray(response)) {
        rawList = response;
      } else if (response?.referrals && Array.isArray(response.referrals)) {
        rawList = response.referrals;
      }

      // STABLE SORT: Newest First (Copy array to avoid read-only TypeError)
      const sortedData = [...rawList].sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_at || a.notifiedAt || 0);
        const dateB = new Date(b.createdAt || b.created_at || b.notifiedAt || 0);
        return dateB - dateA;
      });

      setFullRawData(sortedData);

    } catch (error) {
      console.error("Error fetching referrals:", error);
    }
  };

  useEffect(() => {
    getAllReferralsData(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    applyClientSideFilters(fullRawData);
  }, [fullRawData, applyClientSideFilters]);

  // Handle manual page changes
  useEffect(() => {
    if (filteredDataList.length > 0) {
      const startIndex = (currentPage - 1) * 10;
      setAllReferrals(filteredDataList.slice(startIndex, startIndex + 10));
    }
  }, [currentPage]);

  const fetchMerchantDetails = async (merchantId) => {
    try {
      setActiveMerchantId(merchantId);
      const res = await dispatch(getMerchantById({ id: merchantId })).unwrap();
      if (res?.success === true) {
        setMerchantDetails(res?.data);
        setIsDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching merchant details:", error);
    }
  };

  const handleAddLocationSuccess = () => {
    setAddClicked(false);
    setLocationToEdit(null);
    if (activeMerchantId) {
      fetchMerchantDetails(activeMerchantId);
    }
  };

  const handleSelectCsvLocation = (id) => {
    setSelectedCsvLocationIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchSelectCsvLocations = (locs, isSelected) => {
    if (isSelected) {
      setSelectedCsvLocationIds(locs.map((l) => l._id));
    } else {
      setSelectedCsvLocationIds([]);
    }
  };

  const handleCsvConfirm = async () => {
    if (selectedCsvLocationIds.length === 0) {
      toast.error("Please select at least one location.");
      return;
    }

    const selectedLocations = csvLocations.filter((loc) =>
      selectedCsvLocationIds.includes(loc._id)
    );
    setIsCsvSubmitting(true);

    try {
      const res = await dispatch(
        addFranchise({ merchant: activeMerchantId, locations: selectedLocations })
      ).unwrap();
      if (res?.success) {
        toast.success(
          `${selectedLocations.length} location(s) added successfully`
        );
        setCsvDrawerOpen(false);
        setCsvLocations([]);
        setSelectedCsvLocationIds([]);
        if (activeMerchantId) {
          setTimeout(() => {
            setIsDetailOpen(true);
            fetchMerchantDetails(activeMerchantId);
          }, 300);
        }
      }
    } catch (err) {
      console.error("CSV Confirm error:", err);
    } finally {
      setIsCsvSubmitting(false);
    }
  };

  const handleAcceptedFiles = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isCSV = file.name.toLowerCase().endsWith(".csv");
    if (!isCSV) {
      toast.error("Only .csv files are allowed!");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
      if (lines.length < 2) {
        toast.error("CSV must have headers and at least one row.");
        return;
      }

      const splitCsvLine = (line) => {
        const matches = line.trim().match(/("[^"]*"|[^,]+)(?=,|$)/g);
        if (!matches) return [];
        return matches.map((m) => m.replace(/^"|"$/g, "").trim());
      };

      const headers = (splitCsvLine(lines[0]) || [])
        .map((h) => h.trim().toLowerCase().replace(/\r/g, ""));

      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      const rawRows = lines.slice(1).map((line, index) => {
        const values = splitCsvLine(line).map((v) => v.trim().replace(/\r/g, ""));
        const row = {};
        headers.forEach((key, i) => {
          row[key] = values[i] || "";
        });

        const tradingHours = days.map((day) => ({
          day,
          openTime: row[`${day}_open`] ?? "",
          closeTime: row[`${day}_close`] ?? "",
        }));

        const phoneDigits = String(row["phone"] || "").replace(/\D/g, "");

        return {
          name: row["name"] || "",
          email: row["email"] || "",
          phone: phoneDigits,
          address: row["address"] || "",
          tradingHours,
          googlePlaceId: row["googleplaceid"] || null,
          _id: `temp_${Date.now()}_${index}`,
        };
      });

      const data = rawRows
        .filter((r) => r.name && (r.address || r.googlePlaceId))
        .filter((r, idx, arr) => {
          if (!r.phone) return true;
          return arr.findIndex(x => x.phone === r.phone) === idx;
        });

      if (data.length > 0) {
        (async () => {
          try {
            const payload = {
              merchant: activeMerchantId,
              locations: data
            };
            const resultAction = await dispatch(addFranchise(payload)).unwrap();
            if (resultAction?.success === true) {
              toast.success(`${data.length} location(s) uploaded successfully`);
              setCsvDrawerOpen(false);
              fetchMerchantDetails(activeMerchantId);
            }
          } catch (err) {
            console.error("CSV Upload failed:", err);
          }
        })();
      }
    };
    reader.readAsText(file);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        setSearchQuery(query);
      }, 500),
    []
  );

  const handleSearchChange = (value) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleCancel = () => setOpen(false);

  const handleOpenChange = (flag) => setOpen(flag);
  const handleSelectionChange = (id, val) => setSelections(prev => ({ ...prev, [id]: val }));
  const closePopup = () => setIsDetailOpen(false);

  return (
    <>
      {loading === true && <Loader />}
      {isDetailOpen && (
        <DetailsModal
          handleClose={closePopup}
          content={
            <PopupContent
              merchantDetails={merchantDetails}
              onAddLocation={() => {
                setLocationToEdit(null);
                setIsDetailOpen(false);
                setAddClicked(true);
              }}
              onUploadCSV={() => {
                setCsvLocations([]);
                setSelectedCsvLocationIds([]);
                setIsDetailOpen(false);
                setCsvDrawerOpen(true);
              }}
              onEditLocation={(store) => {
                setLocationToEdit(store);
                setIsDetailOpen(false);
                setAddClicked(true);
              }}
            />
          }
          popupClassName="p-6 w-[335px] sm:w-[600px] md:w-[728px]"
        />
      )}

      {/* Add Location Drawer */}
      <Drawer
        placement="right"
        onClose={() => {
          setAddClicked(false);
          setLocationToEdit(null);
          setIsDetailOpen(true);
        }}
        open={addClicked}
        width={window.innerWidth < 640 ? "100%" : 520}
        className="add-store-drawer add-location-sidebar"
        rootClassName="modern-drawer-root"
        headerStyle={{ display: "none" }}
        closeIcon={null}
      >
        <div className="drawer-custom-header">
          <div
            className="close-icon-wrapper"
            onClick={() => {
              setAddClicked(false);
              setLocationToEdit(null);
              setIsDetailOpen(true);
            }}
          >
            <BackArrow className="w-5 h-5" />
          </div>
          <div className="drawer-header-content">
            <h2 className="drawer-title">
              {locationToEdit
                ? "Edit store location for "
                : "Add New store location for "}
              <span className="text-[#00ADEF]">
                {merchantDetails?.businessName || merchantDetails?.name}
              </span>
            </h2>
            <p className="drawer-subtitle">
              {locationToEdit
                ? "Update the details for this store location."
                : "Fill in the required details to create a new store location."}
            </p>
          </div>
        </div>

        <AddNewLocation
          allStores={false}
          setLocations={() => { }}
          setSelectedLocations={() => { }}
          setSelectedLocationIds={() => { }}
          setLocationNames={() => { }}
          selectedMerchant={merchantDetails}
          editData={locationToEdit}
          onSuccess={handleAddLocationSuccess}
          isDrawer={true}
        />
      </Drawer>

      {/* Upload CSV Drawer */}
      <Drawer
        placement="right"
        onClose={() => {
          setCsvDrawerOpen(false);
          setIsDetailOpen(true);
        }}
        open={csvDrawerOpen}
        width={window.innerWidth < 640 ? "100%" : 520}
        className="add-store-drawer csv-upload-sidebar"
        rootClassName="modern-drawer-root"
        headerStyle={{ display: "none" }}
        closeIcon={null}
      >
        <div className="drawer-custom-header">
          <div className="close-icon-wrapper" onClick={() => {
            setCsvDrawerOpen(false);
            setIsDetailOpen(true);
          }}>
            <BackArrow className="w-5 h-5" />
          </div>
          <div className="drawer-header-content">
            <h2 className="drawer-title">
              Upload Locations via CSV for <span className="text-[#00ADEF]">{merchantDetails?.businessName || merchantDetails?.name}</span>
            </h2>
            <p className="drawer-subtitle">
              Quickly add multiple store locations by uploading a CSV file.
            </p>
          </div>
        </div>
        <UploadCSVDrawer
          onClose={() => {
            setCsvDrawerOpen(false);
            setCsvLocations([]);
            setSelectedCsvLocationIds([]);
            setIsDetailOpen(true);
          }}
          csvLocations={csvLocations}
          selectedLocationIds={selectedCsvLocationIds}
          onSelectionChange={handleSelectCsvLocation}
          onBatchSelectionChange={handleBatchSelectCsvLocations}
          onConfirm={handleCsvConfirm}
          isSubmitting={isCsvSubmitting}
          onClear={() => {
            setCsvLocations([]);
            setSelectedCsvLocationIds([]);
          }}
          onUploadSuccess={(event) => {
            const file = event?.target?.files?.[0];
            if (!file) return;
            const isCSV = file.name.toLowerCase().endsWith(".csv");
            if (!isCSV) {
              toast.error("Only .csv files are allowed!");
              return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
              const text = e.target.result;
              const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
              if (lines.length < 2) {
                toast.error("CSV must have headers and at least one row.");
                return;
              }
              const splitLine = (line) => {
                const m = line.trim().match(/("[^"]*"|[^,]+)(?=,|$)/g);
                return m
                  ? m.map((x) => x.replace(/^"|"$/g, "").trim())
                  : [];
              };
              const headers = splitLine(lines[0]).map((h) =>
                h.toLowerCase().replace(/\r/g, "")
              );
              const days = [
                "monday",
                "tuesday",
                "wednesday",
                "thursday",
                "friday",
                "saturday",
                "sunday",
              ];
              const data = lines
                .slice(1)
                .map((line, idx) => {
                  const vals = splitLine(line).map((v) => v.replace(/\r/g, ""));
                  const row = {};
                  headers.forEach((k, i) => {
                    row[k] = vals[i] || "";
                  });
                  const tradingHours = days.map((day) => ({
                    day,
                    openTime: row[`${day}_open`] || "",
                    closeTime: row[`${day}_close`] || "",
                  }));
                  return {
                    name: row["name"] || "",
                    email: row["email"] || "",
                    phone: String(row["phone"] || "").replace(/\D/g, ""),
                    address: row["address"] || "",
                    tradingHours,
                    googlePlaceId: row["googleplaceid"] || null,
                    _id: `temp_${Date.now()}_${idx}`,
                  };
                })
                .filter((r) => r.name && (r.address || r.googlePlaceId));
              if (data.length > 0) {
                setCsvLocations(data);
                setSelectedCsvLocationIds(data.map((d) => d._id));
                toast.success(`${data.length} locations parsed from CSV.`);
              }
            };
            reader.readAsText(file);
          }}
          sampleFileUrl="/sample-locations.csv"
        />
      </Drawer>

      <div className="flex flex-col md:flex-row items-center justify-between mt-2 mb-6 gap-4">
        <div className="w-full md:w-auto">
          <div className="hidden md:block">
            <BreadCrumsHeader heading="Referral List" items={items} form={true} />
          </div>
          <h1 className="md:hidden text-[20px] font-bold text-[#303030]">Referral List</h1>
        </div>
        <CreateNewButton
          text={"Refer New Merchant"}
          navigateTo={"/fundraiser/refer-merchant"}
          className="w-full md:w-auto pl-10"
          iconClassName="left-4 top-1/2 transform -translate-y-1/2 w-4 h-4"
        />
      </div>

      <div className="w-full flex flex-row justify-between items-center mt-2 mb-4 gap-3">
        <SearchBar
          parentDivClass="w-full md:w-[400px]"
          divClassName="relative w-full"
          inputClassName="searchBar w-full p-3 pr-10 text-[14px] font-poppins outline-none border border-gray-300 rounded-[5px]"
          iconClassName="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
          placeholder="Search..."
          value={searchInput}
          handleChange={(e) => handleSearchChange(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <FilterButton
            activeCount={activeCount}
            isOpen={open}
            onClick={() => setOpen(true)}
          />
        </div>
        <MuiDrawer
          anchor="right"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          TransitionComponent={Slide}
          SlideProps={{
            appear: true,
            timeout: { enter: 700, exit: 500 },
            easing: {
              enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
              exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: '400px' },
              padding: '0',
              height: '100%'
            }
          }}
        >
          <FilterPanel
            filterData={filterData}
            selections={selections}
            appliedSelections={filterParams}
            handleApply={handleApply}
            handleCancel={handleCancel}
            handleReset={handleReset}
            handleSelectionChange={handleSelectionChange}
            style={{ maxHeight: "unset" }}
          />
        </MuiDrawer>
      </div>

      <ActiveFilterBar
        chips={activeFilterChips}
        onRemove={handleRemoveFilter}
        onClearAll={handleReset}
      />

      <div className="col-span-12 mt-4">
        {/* Desktop View */}
        <TableContainer className="hidden md:block">
          <Table sx={{ borderCollapse: "separate", borderSpacing: "0px 8px", minWidth: "1000px" }}>
            <TableHead className="bg-[#00ADEF]">
              <TableRow className="h-[50px]">
                {columns.map((col, idx) => (
                  <TableCell key={idx} className="!text-[#FFFFFF]">{col.name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allReferrals && allReferrals.length > 0 ? (
                allReferrals.map((row, index) => (
                  <TableRow
                    key={row._id || index}
                    className="h-[50px]"
                    sx={{
                      boxShadow: "0px 4px 4px 0px #00000040",
                      backgroundColor: (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                    }}
                  >
                    <TableCell>{(currentPage - 1) * 10 + index + 1}</TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {row?.name || row?.merchant?.businessName || row?.businessName || "N/A"}
                    </TableCell>
                    <TableCell>
                      {separateDateTimeAndConvertToLocale(row?.createdAt || row?.notifiedAt)?.localDate || "-"}
                    </TableCell>
                    <TableCell>{row?.email || row?.user?.email || "-"}</TableCell>
                    <TableCell>
                      {row?.contactNumber || row?.merchant?.contactPersonNumber || "-"}
                    </TableCell>
                    <TableCell>
                      {(row?.status === "accepted" || row?.status === "approved" || row?.status === "live") 
                        ? <AcceptedIcon /> 
                        : <PendingIcon />}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <TableEmptyState itemName="Referral" />
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile View */}
        <div className="md:hidden flex flex-col gap-4">
          {allReferrals?.map((row, index) => (
            <div
              key={row._id || index}
              className="bg-white p-4 rounded-[10px] shadow-sm border border-[#E0E0E0] flex flex-col gap-3"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[14px] font-[600] text-[#303030]">
                    {row?.name || row?.merchant?.businessName || row?.businessName || "N/A"}
                  </p>
                  <p className="text-[12px] text-[#8C8C8C]">{row?.email || row?.user?.email || "-"}</p>
                </div>
                {(row?.status === "accepted" || row?.status === "approved" || row?.status === "live") 
                  ? <AcceptedIcon /> 
                  : <PendingIcon />}
              </div>
              <div className="flex justify-between text-[12px] text-[#303030]">
                <span>Date: {separateDateTimeAndConvertToLocale(row?.createdAt || row?.notifiedAt)?.localDate || "-"}</span>
                <span>Contact: {row?.contactNumber || row?.merchant?.contactPersonNumber || "-"}</span>
              </div>
            </div>
          ))}
          {!loading && allReferrals?.length === 0 && (
            <TableEmptyState itemName="Referral" />
          )}
        </div>

        {allReferrals?.length > 0 && (
          <Pagination
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
          />
        )}
      </div>
    </>
  );
};

export default FundraiserReferralListTable;
